import React from 'react';
import { motion } from 'framer-motion';
import { Boxes, IndianRupee, TrendingUp, Zap } from 'lucide-react';

const InvestmentOverview = () => {
  return (
    <section id="overview" className="py-24 md:py-32 bg-[#FDFBF7]">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#10B981]">Investment Overview</span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#022C22] mt-4 mb-4">
            High-Yield, Performance-Driven Model
          </h2>
          <p className="text-base leading-relaxed text-[#4B5563] max-w-3xl mx-auto mb-4">
            Deploy capital across 20 smart vending machines in premium locations with transparent revenue sharing
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-4xl mx-auto mt-6">
            <div className="bg-[#D1FAE5] border-l-4 border-[#10B981] rounded-lg p-4 flex-1">
              <p className="text-sm font-semibold text-[#064E3B]">
                🌱 Conscious & Healthy Eating: Promoting nutritious food choices through smart, accessible vending solutions
              </p>
            </div>
            <div className="bg-[#FEF3C7] border-l-4 border-[#F59E0B] rounded-lg p-4 flex-1">
              <p className="text-sm font-semibold text-[#92400E]">
                🚀 Early Mover Advantage: Capturing market share in the rapidly growing smart vending sector
              </p>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6 md:gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="md:col-span-4 lg:col-span-6 bg-white border border-[#E5E7EB] rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 p-8 md:p-12"
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="bg-[#D1FAE5] p-4 rounded-2xl">
                <IndianRupee className="h-8 w-8 text-[#10B981]" />
              </div>
              <div>
                <p className="text-sm text-[#4B5563] font-medium">Total Investment</p>
                <h3 className="text-4xl font-bold text-[#022C22]" style={{ fontFamily: 'Outfit, sans-serif' }}>₹10,00,000</h3>
              </div>
            </div>
            <p className="text-base leading-relaxed text-[#4B5563]">
              Fixed capital deployment for acquiring and installing 20 smart vending machines across strategic high-traffic zones.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="md:col-span-4 lg:col-span-6 bg-white border border-[#E5E7EB] rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 p-8 md:p-12"
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="bg-[#D1FAE5] p-4 rounded-2xl">
                <Boxes className="h-8 w-8 text-[#10B981]" />
              </div>
              <div>
                <p className="text-sm text-[#4B5563] font-medium">Deployment</p>
                <h3 className="text-4xl font-bold text-[#022C22] data-font">20 Machines</h3>
              </div>
            </div>
            <p className="text-base leading-relaxed text-[#4B5563]">
              Diversified asset base installed in offices, societies, hospitals, and tech parks for maximum reach and reduced risk.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="md:col-span-8 lg:col-span-12 bg-[#064E3B] text-white rounded-2xl p-8 md:p-12 shadow-xl"
          >
            <h3 className="text-2xl font-semibold tracking-tight text-white mb-6">Revenue Share Structure</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <TrendingUp className="h-8 w-8 text-[#10B981] mb-4" />
                <p className="text-sm text-[#F3F0E6] mb-2">Primary Revenue Share</p>
                <p className="text-3xl font-bold data-font text-white">10%</p>
                <p className="text-sm text-[#F3F0E6] mt-2">of total monthly sales</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <Zap className="h-8 w-8 text-[#F59E0B] mb-4" />
                <p className="text-sm text-[#F3F0E6] mb-2">Performance Bonus</p>
                <p className="text-3xl font-bold data-font text-white">+1-2%</p>
                <p className="text-sm text-[#F3F0E6] mt-2">on sales above ₹20L/₹25L</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="h-8 w-8 bg-[#10B981] rounded-full flex items-center justify-center mb-4">
                  <span className="text-white font-bold data-font text-lg">12%</span>
                </div>
                <p className="text-sm text-[#F3F0E6] mb-2">Maximum Earning Potential</p>
                <p className="text-3xl font-bold data-font text-white">Upto 12%</p>
                <p className="text-sm text-[#F3F0E6] mt-2">effective revenue share</p>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16"
        >
          <h3 className="text-2xl font-semibold tracking-tight text-[#064E3B] mb-8 text-center">Our Smart Vending Machines</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">
              <div className="aspect-square bg-gradient-to-br from-[#F3F0E6] to-white p-8 flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_vending-returns/artifacts/1mivlzlo_1000050628.png" 
                  alt="Orange LIVENEAT Vending Machine" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-6">
                <h4 className="font-semibold text-[#022C22] mb-2">Premium Model</h4>
                <p className="text-sm text-[#4B5563]">Modern design with touchscreen interface, cashless payment systems, and climate control for fresh products.</p>
              </div>
            </div>
            
            <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">
              <div className="aspect-square bg-gradient-to-br from-[#F3F0E6] to-white p-8 flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_vending-returns/artifacts/rl48t9xc_1000050627.jpg" 
                  alt="Blue Smart Vending Machine" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-6">
                <h4 className="font-semibold text-[#022C22] mb-2">Compact Model</h4>
                <p className="text-sm text-[#4B5563]">Space-efficient design perfect for offices and small spaces, featuring diverse product selections and IoT connectivity.</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InvestmentOverview;
